import { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { SupportChannelWebhookPanel } from '../../components/customerSuccess/SupportChannelWebhookPanel';
import { SupportRoutingRulesField } from '../../components/customerSuccess/SupportRoutingRulesField';
import { RichTextEditor } from '../../components/editor/RichTextEditor';
import { ActiveStatusBadge } from '../../components/ui/ActiveStatusBadge';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { FieldLabel } from '../../components/ui/FieldLabel';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { FormCollapsePanel } from '../../components/ui/FormCollapsePanel';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { DuplicateIconButton } from '../../components/ui/DuplicateIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { PasswordInput } from '../../components/ui/PasswordInput';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { Company, listCompanies } from '../../services/coreCompaniesApi';
import {
  ServiceTicketSlaOption,
  ServiceTicketTeam,
  listTicketSlaOptions,
  listTicketTeams,
} from '../../services/customerSuccessTicketsApi';
import {
  SupportChannelApiSettingsUpsert,
  SupportChannelAssistantSettingsUpsert,
  SupportChannelChatSettingsUpsert,
  SupportChannelWebhookSettingsUpsert,
  emptyWebhookSettings,
  SupportChannelConfig,
  SupportChannelConfigUpsertPayload,
  SupportChannelEmailSettingsUpsert,
  createSupportChannel,
  deleteSupportChannel,
  duplicateSupportChannel,
  emptyApiSettings,
  emptyAssistantSettings,
  emptyChatSettings,
  emptyEmailSettings,
  listSupportChannels,
  testSupportChannelAssistant,
  testSupportChannelSmtp,
  updateSupportChannel,
  ATTENDANCE_MODE_LABELS,
  type SupportChannelAttendanceMode,
} from '../../services/supportChannelsApi';
import { listSurveyForms, SurveyForm } from '../../services/customerSuccessEngagementApi';
import { matchesSurveyType } from '../../utils/matchesSurveyType';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import { channelFieldHelp } from './channelFieldHelp';

type NestedPanel =
  | 'imap'
  | 'smtp'
  | 'assistantRequest'
  | 'assistantAuth'
  | 'assistantReply'
  | 'apiRequest'
  | 'apiOutbound'
  | 'apiAuth'
  | 'apiWebhook'
  | 'chatConnection'
  | 'chatWebhook';

const emptyForm: SupportChannelConfigUpsertPayload = {
  channelType: 'Api',
  name: '',
  isEnabled: true,
  externalInboxId: '',
  emailSettings: emptyEmailSettings(),
  apiSettings: emptyApiSettings(),
  chatSettings: emptyChatSettings(),
  assistantSettings: emptyAssistantSettings(),
  webhookSettings: emptyWebhookSettings(),
  attendanceMode: 'Manual',
  defaultCompanyId: '',
  outboundChannelConfigId: '',
  routingRulesJson: '',
  csatEnabled: false,
  npsEnabled: false,
  csatSurveyFormId: '',
  npsSurveyFormId: '',
  csatSendOnResolved: false,
  csatSendOnClosed: false,
  csatSendOnArchived: false,
};

const CHANNEL_LABELS: Record<string, string> = {
  Api: 'API',
  Email: 'E-mail',
  Chat: 'Chat',
};

const ATTENDANCE_MODES: SupportChannelAttendanceMode[] = [
  'Manual',
  'AutoReply',
  'Assistant',
  'ExternalBot',
];

function optionalText(value?: string | null): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function mapEmailSettingsFromChannel(channel: SupportChannelConfig): SupportChannelEmailSettingsUpsert {
  const email = channel.emailSettings;
  return {
    imapHost: email?.imapHost ?? '',
    imapPort: email?.imapPort ?? 993,
    imapUsername: email?.imapUsername ?? '',
    imapPassword: '',
    imapUseSsl: email?.imapUseSsl ?? true,
    smtpHost: email?.smtpHost ?? '',
    smtpPort: email?.smtpPort ?? 587,
    smtpUsername: email?.smtpUsername ?? '',
    smtpPassword: '',
    smtpUseTls: email?.smtpUseTls ?? true,
    smtpFromAddress: email?.smtpFromAddress ?? '',
    smtpFromName: email?.smtpFromName ?? '',
    signatureHtml: email?.signatureHtml ?? '',
  };
}

function mapApiSettingsFromChannel(channel: SupportChannelConfig): SupportChannelApiSettingsUpsert {
  const api = channel.apiSettings;
  const defaults = emptyApiSettings();
  return {
    mode: api?.mode ?? defaults.mode,
    method: api?.method ?? defaults.method,
    url: api?.url ?? '',
    outboundUrl: api?.outboundUrl ?? '',
    outboundMethod: api?.outboundMethod ?? defaults.outboundMethod,
    outboundBodyTemplate: api?.outboundBodyTemplate ?? defaults.outboundBodyTemplate,
    headersJson: api?.headersJson ?? defaults.headersJson,
    authType: api?.authType ?? defaults.authType,
    authUsername: api?.authUsername ?? '',
    authSecret: '',
    apiKeyHeaderName: api?.apiKeyHeaderName ?? defaults.apiKeyHeaderName,
    webhookSecret: '',
  };
}

function mapChatSettingsFromChannel(channel: SupportChannelConfig): SupportChannelChatSettingsUpsert {
  const chat = channel.chatSettings;
  return {
    baseUrl: chat?.baseUrl ?? '',
    accountId: chat?.accountId ?? 0,
    inboxId: chat?.inboxId ?? channel.externalInboxId ?? '',
    assistantName: chat?.assistantName ?? '',
    apiAccessToken: '',
    webhookSecret: '',
  };
}

function mapWebhookSettingsFromChannel(
  channel: SupportChannelConfig,
): SupportChannelWebhookSettingsUpsert {
  const webhook = channel.webhookSettings;
  const defaults = emptyWebhookSettings();
  return {
    enabled: webhook?.enabled ?? false,
    method: webhook?.method ?? defaults.method,
    url: webhook?.url ?? '',
    headersJson: webhook?.headersJson ?? defaults.headersJson,
    bodyTemplate: webhook?.bodyTemplate ?? defaults.bodyTemplate,
    authType: webhook?.authType ?? defaults.authType,
    authUsername: webhook?.authUsername ?? '',
    authSecret: '',
    apiKeyHeaderName: webhook?.apiKeyHeaderName ?? defaults.apiKeyHeaderName,
    statusMap: (webhook?.statusMap ?? []).map((entry) => ({
      goStatus: entry.goStatus,
      externalStatus: entry.externalStatus,
    })),
  };
}

function mapAssistantSettingsFromChannel(channel: SupportChannelConfig): SupportChannelAssistantSettingsUpsert {
  const assistant = channel.assistantSettings;
  const defaults = emptyAssistantSettings();
  return {
    enabled: assistant?.enabled ?? false,
    method: assistant?.method ?? defaults.method,
    url: assistant?.url ?? '',
    headersJson: assistant?.headersJson ?? defaults.headersJson,
    bodyTemplate: assistant?.bodyTemplate ?? defaults.bodyTemplate,
    authType: assistant?.authType ?? defaults.authType,
    authUsername: assistant?.authUsername ?? '',
    authSecret: '',
    apiKeyHeaderName: assistant?.apiKeyHeaderName ?? defaults.apiKeyHeaderName,
    authRequestMethod: assistant?.authRequestMethod ?? defaults.authRequestMethod,
    authRequestUrl: assistant?.authRequestUrl ?? '',
    authRequestHeadersJson: assistant?.authRequestHeadersJson ?? defaults.authRequestHeadersJson,
    authRequestBody: assistant?.authRequestBody ?? '',
    authRequestAuthType: assistant?.authRequestAuthType ?? defaults.authRequestAuthType,
    tokenJsonPath: assistant?.tokenJsonPath ?? defaults.tokenJsonPath,
    tokenApplyAs: assistant?.tokenApplyAs ?? defaults.tokenApplyAs,
    tokenHeaderName: assistant?.tokenHeaderName ?? defaults.tokenHeaderName,
    responseTextJsonPath: assistant?.responseTextJsonPath ?? defaults.responseTextJsonPath,
    responseThreadIdJsonPath: assistant?.responseThreadIdJsonPath ?? defaults.responseThreadIdJsonPath,
    responseHandoffJsonPath: assistant?.responseHandoffJsonPath ?? defaults.responseHandoffJsonPath,
    historyMessageCount: assistant?.historyMessageCount ?? defaults.historyMessageCount,
    escalateToHumanOnFailure:
      assistant?.escalateToHumanOnFailure ?? defaults.escalateToHumanOnFailure,
  };
}

export function ChannelsPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('customer-success.channels');
  const [items, setItems] = useState<SupportChannelConfig[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [teams, setTeams] = useState<ServiceTicketTeam[]>([]);
  const [slaOptions, setSlaOptions] = useState<ServiceTicketSlaOption[]>([]);
  const [surveyForms, setSurveyForms] = useState<SurveyForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<SupportChannelConfigUpsertPayload>(emptyForm);
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } = useCollapsibleItemForm();
  const [saving, setSaving] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [testingAssistant, setTestingAssistant] = useState(false);
  const [smtpTestTo, setSmtpTestTo] = useState('');
  const [openPanels, setOpenPanels] = useState<Partial<Record<NestedPanel, boolean>>>({});

  const emailSettings = form.emailSettings ?? emptyEmailSettings();
  const apiSettings = form.apiSettings ?? emptyApiSettings();
  const chatSettings = form.chatSettings ?? emptyChatSettings();
  const assistantSettings = form.assistantSettings ?? emptyAssistantSettings();
  const editingChannel = editingId ? items.find((item) => item.id === editingId) : undefined;

  async function loadData(): Promise<void> {
    setLoading(true);
    try {
      const [channels, companyResult, teamList, slas, forms] = await Promise.all([
        listSupportChannels(),
        listCompanies({ activeOnly: true, page: 1, pageSize: 200 }),
        listTicketTeams(),
        listTicketSlaOptions(),
        listSurveyForms(),
      ]);
      setItems(channels);
      setCompanies(companyResult.items);
      setTeams(teamList);
      setSlaOptions(slas);
      setSurveyForms(forms);
    } catch {
      toast.error('Falha ao carregar canais de suporte.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData();
  }, []);

  function resetForm(): void {
    setEditingId(null);
    setForm({
      ...emptyForm,
      emailSettings: emptyEmailSettings(),
      apiSettings: emptyApiSettings(),
      chatSettings: emptyChatSettings(),
      assistantSettings: emptyAssistantSettings(),
      webhookSettings: emptyWebhookSettings(),
    });
    setOpenPanels({});
    setSmtpTestTo('');
    setTestingSmtp(false);
  }

  function startEdit(channel: SupportChannelConfig): void {
    expandForm();
    setEditingId(channel.id);
    setOpenPanels({});
    setSmtpTestTo('');
    setTestingSmtp(false);
    setForm({
      channelType: channel.channelType,
      name: channel.name,
      isEnabled: channel.isEnabled,
      externalInboxId: channel.externalInboxId ?? '',
      emailSettings: mapEmailSettingsFromChannel(channel),
      apiSettings: mapApiSettingsFromChannel(channel),
      chatSettings: mapChatSettingsFromChannel(channel),
      assistantSettings: mapAssistantSettingsFromChannel(channel),
      webhookSettings: mapWebhookSettingsFromChannel(channel),
      attendanceMode: channel.attendanceMode || 'Manual',
      defaultCompanyId: channel.defaultCompanyId,
      defaultAssignedTeamId: optionalText(channel.defaultAssignedTeamId),
      defaultSlaId: optionalText(channel.defaultSlaId),
      outboundChannelConfigId: channel.outboundChannelConfigId ?? '',
      routingRulesJson: channel.routingRulesJson ?? '',
      csatEnabled: channel.csatEnabled ?? false,
      npsEnabled: channel.npsEnabled ?? false,
      csatSurveyFormId: channel.csatSurveyFormId ?? '',
      npsSurveyFormId: channel.npsSurveyFormId ?? '',
      csatSendOnResolved: channel.csatSendOnResolved ?? false,
      csatSendOnClosed: channel.csatSendOnClosed ?? false,
      csatSendOnArchived: channel.csatSendOnArchived ?? false,
    });
  }

  function togglePanel(panel: NestedPanel): void {
    setOpenPanels((current) => ({ ...current, [panel]: !current[panel] }));
  }

  function patchEmail(patch: Partial<SupportChannelEmailSettingsUpsert>): void {
    setForm({
      ...form,
      emailSettings: { ...emailSettings, ...patch },
    });
  }

  function patchApi(patch: Partial<SupportChannelApiSettingsUpsert>): void {
    setForm({
      ...form,
      apiSettings: { ...apiSettings, ...patch },
    });
  }

  function patchChat(patch: Partial<SupportChannelChatSettingsUpsert>): void {
    const next = { ...chatSettings, ...patch };
    setForm({
      ...form,
      chatSettings: next,
      externalInboxId: patch.inboxId !== undefined ? patch.inboxId : form.externalInboxId,
    });
  }

  function patchAssistant(patch: Partial<SupportChannelAssistantSettingsUpsert>): void {
    setForm({
      ...form,
      assistantSettings: { ...assistantSettings, ...patch },
    });
  }

  function setAttendanceMode(mode: SupportChannelAttendanceMode): void {
    setForm({
      ...form,
      attendanceMode: mode,
      assistantSettings: {
        ...assistantSettings,
        enabled: mode === 'Assistant',
      },
    });
    if (mode !== 'Assistant') {
      setOpenPanels((current) => ({
        ...current,
        assistantRequest: false,
        assistantAuth: false,
        assistantReply: false,
      }));
    }
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    const mode = (form.attendanceMode || 'Manual') as SupportChannelAttendanceMode;
    const payload: SupportChannelConfigUpsertPayload = {
      channelType: form.channelType,
      name: form.name.trim(),
      isEnabled: form.isEnabled,
      externalInboxId:
        form.channelType === 'Chat'
          ? optionalText(form.chatSettings?.inboxId || form.externalInboxId)
          : optionalText(form.externalInboxId),
      emailSettings: form.channelType === 'Email' ? form.emailSettings : undefined,
      apiSettings: form.channelType === 'Api' ? form.apiSettings : undefined,
      chatSettings: form.channelType === 'Chat' ? form.chatSettings : undefined,
      attendanceMode: mode,
      assistantSettings: {
        ...assistantSettings,
        enabled: mode === 'Assistant',
      },
      webhookSettings: form.webhookSettings ?? emptyWebhookSettings(),
      defaultCompanyId: form.defaultCompanyId,
      defaultAssignedTeamId: optionalText(form.defaultAssignedTeamId),
      defaultSlaId: optionalText(form.defaultSlaId),
      outboundChannelConfigId: optionalText(form.outboundChannelConfigId),
      routingRulesJson: optionalText(form.routingRulesJson),
      csatEnabled: !!form.csatEnabled,
      npsEnabled: !!form.npsEnabled,
      csatSurveyFormId: optionalText(form.csatSurveyFormId),
      npsSurveyFormId: optionalText(form.npsSurveyFormId),
      csatSendOnResolved: !!form.csatSendOnResolved,
      csatSendOnClosed: !!form.csatSendOnClosed,
      csatSendOnArchived: !!form.csatSendOnArchived,
    };

    setSaving(true);
    try {
      if (editingId) {
        await updateSupportChannel(editingId, payload);
        toast.success('Canal atualizado.');
        collapseForm(resetForm);
      } else {
        await createSupportChannel(payload);
        toast.success('Canal criado.');
        handleCreateSuccess(resetForm);
      }
      await loadData();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao salvar canal.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(channel: SupportChannelConfig): Promise<void> {
    if (
      !canWrite
      || !(await confirm({
        title: 'Excluir canal',
        message: `Excluir "${channel.name}"?`,
        confirmLabel: 'Excluir',
        tone: 'danger',
      }))
    ) {
      return;
    }

    try {
      await deleteSupportChannel(channel.id);
      toast.success('Canal excluído.');
      if (editingId === channel.id) {
        collapseForm(resetForm);
      }
      await loadData();
    } catch {
      toast.error('Falha ao excluir canal.');
    }
  }

  async function handleDuplicate(channel: SupportChannelConfig): Promise<void> {
    if (
      !canWrite
      || !(await confirm({
        title: 'Duplicar canal',
        message: `Duplicar "${channel.name}"? A cópia nasce desligada para você revisar inbox, credenciais e regras antes de ativar.`,
        confirmLabel: 'Duplicar',
      }))
    ) {
      return;
    }

    setDuplicatingId(channel.id);
    try {
      await duplicateSupportChannel(channel.id);
      toast.success('Canal duplicado.');
      await loadData();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao duplicar canal.'));
    } finally {
      setDuplicatingId(null);
    }
  }

  async function handleTestSmtp(): Promise<void> {
    if (!canWrite || !editingId || testingSmtp) {
      return;
    }

    const destination =
      smtpTestTo.trim()
      || emailSettings.smtpFromAddress?.trim()
      || 'o remetente do canal';
    if (
      !(await confirm({
        title: 'Enviar e-mail de teste',
        message:
          `Enviar um e-mail de teste para ${destination} pelo SMTP já salvo neste canal? `
          + 'Host, senha ou remetente ainda não gravados não entram neste teste.',
        confirmLabel: 'Enviar teste',
      }))
    ) {
      return;
    }

    setTestingSmtp(true);
    try {
      const result = await testSupportChannelSmtp(editingId, smtpTestTo);
      toast.success(`E-mail de teste enviado para ${result.sentTo}.`);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao enviar e-mail de teste.'));
    } finally {
      setTestingSmtp(false);
    }
  }

  async function handleTestAssistant(): Promise<void> {
    if (!canWrite || !editingId || testingAssistant) {
      return;
    }

    if (
      !(await confirm({
        title: 'Testar assistente N1',
        message:
          'Chamar a API do assistente já salva neste canal com um ping de conectividade? '
          + 'Alterações ainda não gravadas não entram neste teste.',
        confirmLabel: 'Testar',
      }))
    ) {
      return;
    }

    setTestingAssistant(true);
    try {
      const result = await testSupportChannelAssistant(editingId);
      if (result.handoff) {
        toast.success('Assistente respondeu com handoff (sem texto útil). Conectividade OK.');
      } else if (result.replyPreview) {
        toast.success(`Assistente OK: ${result.replyPreview}`);
      } else {
        toast.success('Assistente respondeu com sucesso.');
      }
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha no teste do assistente.'));
    } finally {
      setTestingAssistant(false);
    }
  }

  const imapSummary = emailSettings.imapHost
    ? `${emailSettings.imapUsername || 'usuário'} · ${emailSettings.imapHost}:${emailSettings.imapPort}`
    : 'Não configurado';
  const smtpSummary = emailSettings.smtpHost
    ? `${emailSettings.smtpFromAddress || emailSettings.smtpUsername || 'remetente'} · ${emailSettings.smtpHost}`
    : 'Usa SMTP das configurações se vazio';
  const apiRequestSummary = apiSettings.url
    ? `${apiSettings.method} ${apiSettings.url}`
    : `${apiSettings.mode} · sem URL`;
  const apiAuthSummary =
    apiSettings.authType === 'None' ? 'Sem autenticação' : apiSettings.authType;
  const apiWebhookSummary = editingChannel?.apiSettings?.webhookPath
    ?? (editingId ? `/api/v1/integrations/channels/${editingId}/webhook` : 'Disponível após salvar');
  const chatConnectionSummary = chatSettings.baseUrl
    ? `${chatSettings.assistantName || 'Assistente'} · account ${chatSettings.accountId || '—'}`
    : 'Não configurado';
  const chatWebhookSummary = editingChannel?.chatSettings?.webhookPath
    ?? (editingId ? `/api/v1/integrations/channels/${editingId}/webhook` : 'Disponível após salvar');
  const assistantSummary =
    `${assistantSettings.method} ${assistantSettings.url || 'sem URL'} · ${assistantSettings.authType}`;

  return (
    <PageShell>
      <PageHeader
        title="Canais de suporte"
        description="Configure entradas API, e-mail e chat com empresa padrão e regras de triagem."
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar canal' : 'Novo canal'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          editingSummary={
            editingId ? `Editando ${form.name || 'canal selecionado'} — clique para expandir` : undefined
          }
          createSummary="Clique para expandir e cadastrar um novo canal"
          isEditing={!!editingId}
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="erp-form px-4 pb-4 pt-3">
          <div className="space-y-4">
            <div className="erp-form-grid">
              <label className="erp-label">
                <FieldLabel {...channelFieldHelp.name} required>
                  Nome
                </FieldLabel>
                <input
                  className="erp-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </label>
              <label className="erp-label">
                <FieldLabel {...channelFieldHelp.channelType}>Tipo</FieldLabel>
                <select
                  className="erp-input"
                  value={form.channelType}
                  onChange={(e) => {
                    setForm({ ...form, channelType: e.target.value });
                    setOpenPanels({});
                  }}
                >
                  <option value="Api">API</option>
                  <option value="Email">E-mail</option>
                  <option value="Chat">Chat</option>
                </select>
              </label>
              <label className="erp-label">
                <FieldLabel {...channelFieldHelp.attendanceMode} required>
                  Modo de atendimento
                </FieldLabel>
                <select
                  className="erp-input"
                  value={form.attendanceMode || 'Manual'}
                  onChange={(e) => setAttendanceMode(e.target.value as SupportChannelAttendanceMode)}
                >
                  {ATTENDANCE_MODES.map((mode) => (
                    <option key={mode} value={mode}>
                      {ATTENDANCE_MODE_LABELS[mode]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                <FieldLabel {...channelFieldHelp.externalInboxId}>Inbox externo</FieldLabel>
                <input
                  className="erp-input"
                  value={form.externalInboxId ?? ''}
                  onChange={(e) => setForm({ ...form, externalInboxId: e.target.value })}
                  placeholder={
                    form.channelType === 'Email'
                      ? 'suporte@empresa.com'
                      : form.channelType === 'Chat'
                        ? 'Preenchido pelo Inbox ID do chat'
                        : 'ID do inbox ou endereço'
                  }
                  disabled={form.channelType === 'Chat'}
                />
              </label>
              <label className="erp-label">
                <FieldLabel {...channelFieldHelp.defaultCompanyId} required>
                  Empresa padrão
                </FieldLabel>
                <select
                  className="erp-input"
                  value={form.defaultCompanyId}
                  onChange={(e) => setForm({ ...form, defaultCompanyId: e.target.value })}
                  required
                >
                  <option value="">Selecione...</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                <FieldLabel {...channelFieldHelp.defaultAssignedTeamId}>Equipe padrão</FieldLabel>
                <select
                  className="erp-input"
                  value={form.defaultAssignedTeamId ?? ''}
                  onChange={(e) => setForm({ ...form, defaultAssignedTeamId: e.target.value || undefined })}
                >
                  <option value="">Nenhuma</option>
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                <FieldLabel {...channelFieldHelp.defaultSlaId}>SLA padrão</FieldLabel>
                <select
                  className="erp-input"
                  value={form.defaultSlaId ?? ''}
                  onChange={(e) => setForm({ ...form, defaultSlaId: e.target.value || undefined })}
                >
                  <option value="">Nenhum</option>
                  {slaOptions.map((sla) => (
                    <option key={sla.id} value={sla.id}>
                      {sla.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label md:col-span-2">
                <FieldLabel {...channelFieldHelp.outboundChannelConfigId}>Canal de saída</FieldLabel>
                <select
                  className="erp-input"
                  value={form.outboundChannelConfigId ?? ''}
                  onChange={(e) =>
                    setForm({ ...form, outboundChannelConfigId: e.target.value || '' })
                  }
                >
                  <option value="">Mesmo canal (entrada = saída)</option>
                  {items
                    .filter((item) => item.id !== editingId)
                    .map((item) => (
                      <option key={item.id} value={item.id}>
                        {CHANNEL_LABELS[item.channelType] ?? item.channelType} · {item.name}
                      </option>
                    ))}
                </select>
              </label>
            </div>

            <div className="rounded-xl border border-secondary-200 bg-secondary-50/40 p-4 space-y-3">
              <div>
                <p className="text-sm font-medium text-secondary-900">Pesquisas (CSAT / NPS)</p>
                <p className="erp-hint mt-1">
                  O mesmo formulário pode ser vinculado a vários canais. Sem formulário vinculado, o
                  sistema usa o formulário da empresa ou o formulário padrão. O envio automático só
                  ocorre nos momentos marcados abaixo; o envio manual na mesa ignora esses momentos.
                </p>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <label className="flex items-center gap-2 text-sm text-secondary-800">
                  <input
                    type="checkbox"
                    checked={!!form.csatEnabled}
                    onChange={(e) => setForm({ ...form, csatEnabled: e.target.checked })}
                  />
                  Habilitar CSAT neste canal
                </label>
                <label className="flex items-center gap-2 text-sm text-secondary-800">
                  <input
                    type="checkbox"
                    checked={!!form.npsEnabled}
                    onChange={(e) => setForm({ ...form, npsEnabled: e.target.checked })}
                  />
                  Habilitar NPS neste canal
                </label>
                <label className="erp-label">
                  Formulário CSAT
                  <select
                    className="erp-input"
                    value={form.csatSurveyFormId ?? ''}
                    onChange={(e) => setForm({ ...form, csatSurveyFormId: e.target.value })}
                    disabled={!form.csatEnabled}
                  >
                    <option value="">Padrão (empresa ou formulário padrão)</option>
                    {surveyForms
                      .filter((item) => item.isActive && matchesSurveyType(item.surveyType, 'csat'))
                      .map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                          {item.companies?.length
                            ? ` · ${item.companies.map((company) => company.name).join(', ')}`
                            : ' · Padrão'}
                        </option>
                      ))}
                  </select>
                </label>
                <label className="erp-label">
                  Formulário NPS
                  <select
                    className="erp-input"
                    value={form.npsSurveyFormId ?? ''}
                    onChange={(e) => setForm({ ...form, npsSurveyFormId: e.target.value })}
                    disabled={!form.npsEnabled}
                  >
                    <option value="">Padrão (empresa ou formulário padrão)</option>
                    {surveyForms
                      .filter((item) => item.isActive && matchesSurveyType(item.surveyType, 'nps'))
                      .map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                          {item.companies?.length
                            ? ` · ${item.companies.map((company) => company.name).join(', ')}`
                            : ' · Padrão'}
                        </option>
                      ))}
                  </select>
                </label>
              </div>
              <div>
                <p className="text-xs font-medium text-secondary-700 mb-2">
                  Enviar CSAT automaticamente quando
                </p>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 text-sm text-secondary-800">
                    <input
                      type="checkbox"
                      checked={!!form.csatSendOnResolved}
                      disabled={!form.csatEnabled}
                      onChange={(e) =>
                        setForm({ ...form, csatSendOnResolved: e.target.checked })
                      }
                    />
                    Resolvido
                  </label>
                  <label className="flex items-center gap-2 text-sm text-secondary-800">
                    <input
                      type="checkbox"
                      checked={!!form.csatSendOnClosed}
                      disabled={!form.csatEnabled}
                      onChange={(e) => setForm({ ...form, csatSendOnClosed: e.target.checked })}
                    />
                    Fechado
                  </label>
                  <label className="flex items-center gap-2 text-sm text-secondary-800">
                    <input
                      type="checkbox"
                      checked={!!form.csatSendOnArchived}
                      disabled={!form.csatEnabled}
                      onChange={(e) =>
                        setForm({ ...form, csatSendOnArchived: e.target.checked })
                      }
                    />
                    Arquivado (mesa)
                  </label>
                </div>
                {form.csatEnabled
                && !form.csatSendOnResolved
                && !form.csatSendOnClosed
                && !form.csatSendOnArchived ? (
                  <p className="mt-2 text-xs text-amber-800">
                    Selecione ao menos um momento para o envio automático de CSAT.
                  </p>
                ) : null}
              </div>
            </div>

            <SupportChannelWebhookPanel
              value={form.webhookSettings ?? emptyWebhookSettings()}
              hasAuthSecret={!!editingChannel?.webhookSettings?.hasAuthSecret}
              onChange={(webhookSettings) => setForm({ ...form, webhookSettings })}
            />

            {form.channelType === 'Email' ? (
              <div className="space-y-2">
                <FormCollapsePanel
                  title="IMAP (leitura)"
                  summary={emailSettings.imapHost ? imapSummary : 'Opcional · fallback das configurações'}
                  expanded={!!openPanels.imap}
                  onToggle={() => togglePanel('imap')}
                >
                  <div className="space-y-3">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                      <label className="erp-label min-w-0 flex-1">
                        <FieldLabel {...channelFieldHelp.imapHost}>Host IMAP</FieldLabel>
                        <input
                          className="erp-input"
                          value={emailSettings.imapHost ?? ''}
                          onChange={(e) => patchEmail({ imapHost: e.target.value })}
                        />
                      </label>
                      <div className="flex shrink-0 items-end gap-3">
                        <label className="erp-label w-[7.5rem]">
                          <FieldLabel {...channelFieldHelp.imapPort}>Porta</FieldLabel>
                          <input
                            className="erp-input"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={5}
                            value={emailSettings.imapPort}
                            onChange={(e) => {
                              const digits = e.target.value.replace(/\D/g, '').slice(0, 5);
                              if (digits === '') {
                                patchEmail({ imapPort: 993 });
                                return;
                              }
                              const port = Number(digits);
                              if (port >= 1 && port <= 65535) {
                                patchEmail({ imapPort: port });
                              }
                            }}
                          />
                        </label>
                        <label className="mb-2 flex items-center gap-2 whitespace-nowrap text-sm text-secondary-800">
                          <input
                            type="checkbox"
                            checked={emailSettings.imapUseSsl}
                            onChange={(e) => patchEmail({ imapUseSsl: e.target.checked })}
                          />
                          <FieldLabel {...channelFieldHelp.imapUseSsl}>Usar SSL</FieldLabel>
                        </label>
                      </div>
                    </div>
                    <div className="erp-form-grid">
                      <label className="erp-label">
                        <FieldLabel {...channelFieldHelp.imapUsername}>Usuário IMAP</FieldLabel>
                        <input
                          className="erp-input"
                          value={emailSettings.imapUsername ?? ''}
                          onChange={(e) => patchEmail({ imapUsername: e.target.value })}
                        />
                      </label>
                      <label className="erp-label">
                        <FieldLabel {...channelFieldHelp.imapPassword}>Senha IMAP</FieldLabel>
                        <PasswordInput
                          className="erp-input pr-11 !mt-0"
                          wrapperClassName="mt-1 w-full"
                          autoComplete="new-password"
                          value={emailSettings.imapPassword ?? ''}
                          onChange={(e) => patchEmail({ imapPassword: e.target.value })}
                          placeholder={
                            editingChannel?.emailSettings?.hasImapPassword
                              ? 'Deixe em branco para manter'
                              : undefined
                          }
                        />
                      </label>
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-secondary-500">
                    Opcional. Se vazio, a leitura usa o IMAP das configurações do sistema.
                  </p>
                </FormCollapsePanel>

                <FormCollapsePanel
                  title="SMTP (envio)"
                  summary={smtpSummary}
                  expanded={!!openPanels.smtp}
                  onToggle={() => togglePanel('smtp')}
                >
                  <div className="space-y-3">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                      <label className="erp-label min-w-0 flex-1">
                        <FieldLabel {...channelFieldHelp.smtpHost}>Host SMTP</FieldLabel>
                        <input
                          className="erp-input"
                          value={emailSettings.smtpHost ?? ''}
                          onChange={(e) => patchEmail({ smtpHost: e.target.value })}
                        />
                      </label>
                      <div className="flex shrink-0 items-end gap-3">
                        <label className="erp-label w-[7.5rem]">
                          <FieldLabel {...channelFieldHelp.smtpPort}>Porta</FieldLabel>
                          <input
                            className="erp-input"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={5}
                            value={emailSettings.smtpPort}
                            onChange={(e) => {
                              const digits = e.target.value.replace(/\D/g, '').slice(0, 5);
                              if (digits === '') {
                                patchEmail({ smtpPort: 587 });
                                return;
                              }
                              const port = Number(digits);
                              if (port >= 1 && port <= 65535) {
                                patchEmail({ smtpPort: port });
                              }
                            }}
                          />
                        </label>
                        <label className="mb-2 flex items-center gap-2 whitespace-nowrap text-sm text-secondary-800">
                          <input
                            type="checkbox"
                            checked={emailSettings.smtpUseTls}
                            onChange={(e) => patchEmail({ smtpUseTls: e.target.checked })}
                          />
                          <FieldLabel {...channelFieldHelp.smtpUseTls}>Usar TLS</FieldLabel>
                        </label>
                      </div>
                    </div>
                    <div className="erp-form-grid">
                      <label className="erp-label">
                        <FieldLabel {...channelFieldHelp.smtpUsername}>Usuário SMTP</FieldLabel>
                        <input
                          className="erp-input"
                          value={emailSettings.smtpUsername ?? ''}
                          onChange={(e) => patchEmail({ smtpUsername: e.target.value })}
                        />
                      </label>
                      <label className="erp-label">
                        <FieldLabel {...channelFieldHelp.smtpPassword}>Senha SMTP</FieldLabel>
                        <PasswordInput
                          className="erp-input pr-11 !mt-0"
                          wrapperClassName="mt-1 w-full"
                          autoComplete="new-password"
                          value={emailSettings.smtpPassword ?? ''}
                          onChange={(e) => patchEmail({ smtpPassword: e.target.value })}
                          placeholder={
                            editingChannel?.emailSettings?.hasSmtpPassword
                              ? 'Deixe em branco para manter'
                              : undefined
                          }
                        />
                      </label>
                      <label className="erp-label">
                        <FieldLabel {...channelFieldHelp.smtpFromAddress}>Remetente (From)</FieldLabel>
                        <input
                          className="erp-input"
                          value={emailSettings.smtpFromAddress ?? ''}
                          onChange={(e) => patchEmail({ smtpFromAddress: e.target.value })}
                          placeholder="suporte@empresa.com"
                        />
                      </label>
                      <label className="erp-label">
                        <FieldLabel {...channelFieldHelp.smtpFromName}>Nome do remetente</FieldLabel>
                        <input
                          className="erp-input"
                          value={emailSettings.smtpFromName ?? ''}
                          onChange={(e) => patchEmail({ smtpFromName: e.target.value })}
                        />
                      </label>
                      <div className="erp-label sm:col-span-2">
                        <FieldLabel {...channelFieldHelp.signatureHtml}>Assinatura</FieldLabel>
                        <div className="mt-1">
                          <RichTextEditor
                            value={emailSettings.signatureHtml ?? ''}
                            onChange={(html) => patchEmail({ signatureHtml: html })}
                            minHeight="140px"
                            allowLocalImages
                            placeholder="Texto, formatação e imagem da assinatura…"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 rounded-xl border border-secondary-200 bg-secondary-50 p-3">
                      <p className="mb-2 text-xs text-secondary-500">
                        O teste usa o SMTP já gravado neste canal. Salve host, usuário, senha e
                        remetente antes de testar.
                      </p>
                      {editingId ? (
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                          <label className="erp-label min-w-0 flex-1">
                            <FieldLabel {...channelFieldHelp.smtpTestTo}>E-mail de destino</FieldLabel>
                            <input
                              className="erp-input"
                              type="email"
                              value={smtpTestTo}
                              onChange={(e) => setSmtpTestTo(e.target.value)}
                              placeholder={
                                emailSettings.smtpFromAddress?.trim()
                                || 'remetente do canal'
                              }
                              disabled={!canWrite || testingSmtp}
                            />
                          </label>
                          <button
                            type="button"
                            className="erp-btn-secondary erp-btn-sm whitespace-nowrap"
                            disabled={!canWrite || testingSmtp || saving}
                            onClick={() => void handleTestSmtp()}
                          >
                            {testingSmtp ? 'Enviando...' : 'Enviar e-mail de teste'}
                          </button>
                        </div>
                      ) : (
                        <p className="text-xs text-secondary-500">
                          Salve o canal para testar o SMTP.
                        </p>
                      )}
                    </div>
                  </div>
                </FormCollapsePanel>
              </div>
            ) : null}

            {(form.channelType === 'Email'
              || form.channelType === 'Api'
              || form.channelType === 'Chat')
            && form.attendanceMode === 'Assistant' ? (
              <div className="space-y-2">
                <p className="text-sm text-secondary-600">
                  Modo Assistente: configure a API do N1. O Go chama este endpoint após a mensagem virar chamado.
                </p>
                <div className="erp-form-grid">
                    <label className="erp-label">
                      <FieldLabel {...channelFieldHelp.historyMessageCount}>
                        Mensagens de histórico (0–50)
                      </FieldLabel>
                      <input
                        className="erp-input"
                        type="number"
                        min={0}
                        max={50}
                        value={assistantSettings.historyMessageCount}
                        onChange={(e) =>
                          patchAssistant({
                            historyMessageCount: Math.max(
                              0,
                              Math.min(50, Number.parseInt(e.target.value, 10) || 0),
                            ),
                          })
                        }
                      />
                    </label>
                    <label className="flex items-center gap-2 text-sm self-end pb-2">
                      <input
                        type="checkbox"
                        checked={assistantSettings.escalateToHumanOnFailure}
                        onChange={(e) =>
                          patchAssistant({ escalateToHumanOnFailure: e.target.checked })
                        }
                      />
                      <FieldLabel {...channelFieldHelp.escalateToHumanOnFailure}>
                        Em falha, pausar N1 e notificar equipe
                      </FieldLabel>
                    </label>
                  </div>
                {editingId ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="erp-btn-secondary text-sm"
                      disabled={!canWrite || testingAssistant || saving}
                      onClick={() => void handleTestAssistant()}
                    >
                      {testingAssistant ? 'Testando...' : 'Testar assistente'}
                    </button>
                    <span className="text-xs text-secondary-500">
                      Usa a config já salva (salve antes se mudou URL/auth).
                    </span>
                  </div>
                ) : (
                  <p className="text-xs text-secondary-500">
                    Salve o canal para testar a conectividade do assistente.
                  </p>
                )}
                <FormCollapsePanel
                  title="Request do assistente"
                  summary={assistantSummary}
                  expanded={!!openPanels.assistantRequest}
                  onToggle={() => togglePanel('assistantRequest')}
                >
                  <p className="mb-3 text-xs text-secondary-500">
                    Request HTTP como no Postman. Placeholders no body:{' '}
                    <code>{'{{message}}'}</code>, <code>{'{{subject}}'}</code>, <code>{'{{fromAddress}}'}</code>,{' '}
                    <code>{'{{ticketCode}}'}</code>, <code>{'{{ticketId}}'}</code>, <code>{'{{threadId}}'}</code>,{' '}
                    <code>{'{{history}}'}</code>. O de-para da resposta fica no painel{' '}
                    <strong>De-para da resposta</strong>.
                  </p>
                  <div className="erp-form-grid">
                    <label className="erp-label">
                      <FieldLabel {...channelFieldHelp.assistantMethod}>Método</FieldLabel>
                      <select
                        className="erp-input"
                        value={assistantSettings.method}
                        onChange={(e) => patchAssistant({ method: e.target.value })}
                      >
                        <option value="GET">GET</option>
                        <option value="POST">POST</option>
                        <option value="PUT">PUT</option>
                        <option value="PATCH">PATCH</option>
                        <option value="DELETE">DELETE</option>
                      </select>
                    </label>
                    <label className="erp-label md:col-span-2">
                      <FieldLabel {...channelFieldHelp.assistantUrl} required>
                        URL
                      </FieldLabel>
                      <input
                        className="erp-input"
                        value={assistantSettings.url ?? ''}
                        onChange={(e) => patchAssistant({ url: e.target.value })}
                        placeholder="https://provedor.exemplo.com/api/chat"
                        required
                      />
                    </label>
                    <label className="erp-label md:col-span-2">
                      <FieldLabel {...channelFieldHelp.assistantHeaders}>Headers (JSON)</FieldLabel>
                      <textarea
                        className="erp-input min-h-[88px] font-mono text-xs"
                        value={assistantSettings.headersJson ?? ''}
                        onChange={(e) => patchAssistant({ headersJson: e.target.value })}
                        spellCheck={false}
                      />
                    </label>
                    <label className="erp-label md:col-span-2">
                      <FieldLabel {...channelFieldHelp.assistantBody}>Body</FieldLabel>
                      <textarea
                        className="erp-input min-h-[140px] font-mono text-xs"
                        value={assistantSettings.bodyTemplate ?? ''}
                        onChange={(e) => patchAssistant({ bodyTemplate: e.target.value })}
                        spellCheck={false}
                        placeholder={'{\n  "message": "{{message}}"\n}'}
                      />
                    </label>
                  </div>
                </FormCollapsePanel>

                <FormCollapsePanel
                  title="Autenticação do assistente"
                  summary={assistantSettings.authType === 'None' ? 'Sem autenticação' : assistantSettings.authType}
                  expanded={!!openPanels.assistantAuth}
                  onToggle={() => togglePanel('assistantAuth')}
                >
                  <div className="erp-form-grid">
                    <label className="erp-label">
                      <FieldLabel {...channelFieldHelp.assistantAuthType}>Tipo</FieldLabel>
                      <select
                        className="erp-input"
                        value={assistantSettings.authType}
                        onChange={(e) => patchAssistant({ authType: e.target.value })}
                      >
                        <option value="None">None</option>
                        <option value="Bearer">Bearer Token</option>
                        <option value="Basic">Basic</option>
                        <option value="ApiKey">API Key</option>
                        <option value="AuthApi">API de autenticação (obter token)</option>
                      </select>
                    </label>
                    {assistantSettings.authType === 'Basic'
                      || (assistantSettings.authType === 'AuthApi'
                        && assistantSettings.authRequestAuthType === 'Basic') ? (
                      <label className="erp-label">
                        <FieldLabel {...channelFieldHelp.assistantAuthUsername}>Usuário</FieldLabel>
                        <input
                          className="erp-input"
                          value={assistantSettings.authUsername ?? ''}
                          onChange={(e) => patchAssistant({ authUsername: e.target.value })}
                        />
                      </label>
                    ) : null}
                    {assistantSettings.authType === 'ApiKey'
                      || (assistantSettings.authType === 'AuthApi'
                        && assistantSettings.authRequestAuthType === 'ApiKey') ? (
                      <label className="erp-label">
                        <FieldLabel {...channelFieldHelp.assistantApiKeyHeader}>Nome do header</FieldLabel>
                        <input
                          className="erp-input"
                          value={assistantSettings.apiKeyHeaderName ?? ''}
                          onChange={(e) => patchAssistant({ apiKeyHeaderName: e.target.value })}
                          placeholder="X-Api-Key"
                        />
                      </label>
                    ) : null}
                    {assistantSettings.authType !== 'None' ? (
                      <label className="erp-label">
                        <FieldLabel {...channelFieldHelp.assistantAuthSecret}>
                          {assistantSettings.authType === 'Basic'
                            || (assistantSettings.authType === 'AuthApi'
                              && assistantSettings.authRequestAuthType === 'Basic')
                            ? 'Senha'
                            : 'Secret / Token'}
                        </FieldLabel>
                        <PasswordInput
                          className="erp-input pr-11 !mt-0"
                          wrapperClassName="mt-1 w-full"
                          autoComplete="new-password"
                          value={assistantSettings.authSecret ?? ''}
                          onChange={(e) => patchAssistant({ authSecret: e.target.value })}
                          placeholder={
                            editingChannel?.assistantSettings?.hasAuthSecret
                              ? 'Deixe em branco para manter'
                              : undefined
                          }
                          required={
                            assistantSettings.authType !== 'None'
                            && assistantSettings.authType !== 'AuthApi'
                            && !editingChannel?.assistantSettings?.hasAuthSecret
                          }
                        />
                      </label>
                    ) : null}
                  </div>

                  {assistantSettings.authType === 'AuthApi' ? (
                    <div className="mt-4 space-y-3 border-t border-secondary-200 pt-4">
                      <p className="text-xs text-secondary-500">
                        O Go chama esta API primeiro, lê o token na resposta e aplica na request do assistente.
                        Use <code>{'{{authSecret}}'}</code> e <code>{'{{authUsername}}'}</code> no body/headers se
                        precisar.
                      </p>
                      <div className="erp-form-grid">
                        <label className="erp-label">
                          <FieldLabel {...channelFieldHelp.authRequestMethod}>Método</FieldLabel>
                          <select
                            className="erp-input"
                            value={assistantSettings.authRequestMethod}
                            onChange={(e) => patchAssistant({ authRequestMethod: e.target.value })}
                          >
                            <option value="GET">GET</option>
                            <option value="POST">POST</option>
                            <option value="PUT">PUT</option>
                          </select>
                        </label>
                        <label className="erp-label md:col-span-2">
                          <FieldLabel {...channelFieldHelp.authRequestUrl} required>
                            URL da API de autenticação
                          </FieldLabel>
                          <input
                            className="erp-input"
                            value={assistantSettings.authRequestUrl ?? ''}
                            onChange={(e) => patchAssistant({ authRequestUrl: e.target.value })}
                            placeholder="https://provedor.exemplo.com/api/auth/token"
                            required
                          />
                        </label>
                        <label className="erp-label">
                          <FieldLabel {...channelFieldHelp.authRequestAuthType}>Auth desta API</FieldLabel>
                          <select
                            className="erp-input"
                            value={assistantSettings.authRequestAuthType}
                            onChange={(e) => patchAssistant({ authRequestAuthType: e.target.value })}
                          >
                            <option value="None">None (credencial no body)</option>
                            <option value="Bearer">Bearer</option>
                            <option value="Basic">Basic</option>
                            <option value="ApiKey">API Key</option>
                          </select>
                        </label>
                        <label className="erp-label">
                          <FieldLabel {...channelFieldHelp.tokenJsonPath}>Campo JSON do token</FieldLabel>
                          <input
                            className="erp-input"
                            value={assistantSettings.tokenJsonPath ?? ''}
                            onChange={(e) => patchAssistant({ tokenJsonPath: e.target.value })}
                            placeholder="access_token"
                          />
                        </label>
                        <label className="erp-label">
                          <FieldLabel {...channelFieldHelp.tokenApplyAs}>Aplicar token como</FieldLabel>
                          <select
                            className="erp-input"
                            value={assistantSettings.tokenApplyAs}
                            onChange={(e) => patchAssistant({ tokenApplyAs: e.target.value })}
                          >
                            <option value="Bearer">Bearer</option>
                            <option value="Basic">Basic</option>
                            <option value="Header">Header customizado</option>
                          </select>
                        </label>
                        {assistantSettings.tokenApplyAs === 'Header' ? (
                          <label className="erp-label">
                            <FieldLabel {...channelFieldHelp.tokenHeaderName}>Nome do header</FieldLabel>
                            <input
                              className="erp-input"
                              value={assistantSettings.tokenHeaderName ?? ''}
                              onChange={(e) => patchAssistant({ tokenHeaderName: e.target.value })}
                              placeholder="Authorization"
                            />
                          </label>
                        ) : null}
                        <label className="erp-label md:col-span-2">
                          <FieldLabel {...channelFieldHelp.authRequestHeaders}>Headers da auth (JSON)</FieldLabel>
                          <textarea
                            className="erp-input min-h-[72px] font-mono text-xs"
                            value={assistantSettings.authRequestHeadersJson ?? ''}
                            onChange={(e) => patchAssistant({ authRequestHeadersJson: e.target.value })}
                            spellCheck={false}
                          />
                        </label>
                        <label className="erp-label md:col-span-2">
                          <FieldLabel {...channelFieldHelp.authRequestBody}>Body da auth</FieldLabel>
                          <textarea
                            className="erp-input min-h-[100px] font-mono text-xs"
                            value={assistantSettings.authRequestBody ?? ''}
                            onChange={(e) => patchAssistant({ authRequestBody: e.target.value })}
                            spellCheck={false}
                            placeholder={'{\n  "clientId": "{{authUsername}}",\n  "clientSecret": "{{authSecret}}"\n}'}
                          />
                        </label>
                      </div>
                    </div>
                  ) : null}
                </FormCollapsePanel>

                <FormCollapsePanel
                  title="De-para da resposta"
                  summary={`${assistantSettings.responseTextJsonPath || 'text'} → text`}
                  expanded={!!openPanels.assistantReply}
                  onToggle={() => togglePanel('assistantReply')}
                >
                  <p className="mb-3 text-xs text-secondary-500">
                    O contrato interno do Go é <code>text</code>, <code>threadId</code> e <code>handoff</code>.
                    Informe os caminhos JSON do provedor. Campos extras (como <code>sources</code>) são ignorados.
                    JSON único ou SSE <code>data:</code>.
                  </p>
                  <div className="erp-form-grid">
                    <label className="erp-label">
                      <FieldLabel {...channelFieldHelp.responseTextJsonPath} required>
                        Campo JSON do texto
                      </FieldLabel>
                      <input
                        className="erp-input"
                        value={assistantSettings.responseTextJsonPath ?? ''}
                        onChange={(e) => patchAssistant({ responseTextJsonPath: e.target.value })}
                        placeholder="textResponse"
                      />
                    </label>
                    <label className="erp-label">
                      <FieldLabel {...channelFieldHelp.responseThreadIdJsonPath}>Campo JSON do threadId</FieldLabel>
                      <input
                        className="erp-input"
                        value={assistantSettings.responseThreadIdJsonPath ?? ''}
                        onChange={(e) => patchAssistant({ responseThreadIdJsonPath: e.target.value })}
                        placeholder="threadId"
                      />
                    </label>
                    <label className="erp-label">
                      <FieldLabel {...channelFieldHelp.responseHandoffJsonPath}>Campo JSON do handoff</FieldLabel>
                      <input
                        className="erp-input"
                        value={assistantSettings.responseHandoffJsonPath ?? ''}
                        onChange={(e) => patchAssistant({ responseHandoffJsonPath: e.target.value })}
                        placeholder="handoff"
                      />
                    </label>
                  </div>
                </FormCollapsePanel>
              </div>
            ) : (form.channelType === 'Email'
              || form.channelType === 'Api'
              || form.channelType === 'Chat')
              && form.attendanceMode !== 'Assistant' ? (
              <p className="rounded-md border border-secondary-200 bg-secondary-50 px-3 py-2 text-sm text-secondary-600">
                {form.attendanceMode === 'AutoReply'
                  ? 'Modo Auto-reply: o Go envia ack de abertura (e after-hours se configurado). Sem N1.'
                  : form.attendanceMode === 'ExternalBot'
                    ? 'Modo Bot externo: o Go registra o chamado; a conversa automática fica na plataforma do chat. Sem N1 nem ack do Go.'
                    : 'Modo Manual: só registra na mesa. Humano responde; sem N1 nem ack automático.'}
              </p>
            ) : null}

            {form.channelType === 'Api' ? (
              <div className="space-y-2">
                <FormCollapsePanel
                  title="Requisição"
                  summary={apiRequestSummary}
                  expanded={!!openPanels.apiRequest}
                  onToggle={() => togglePanel('apiRequest')}
                >
                  <div className="erp-form-grid">
                    <label className="erp-label">
                      <FieldLabel {...channelFieldHelp.apiMode}>Modo</FieldLabel>
                      <select
                        className="erp-input"
                        value={apiSettings.mode}
                        onChange={(e) => patchApi({ mode: e.target.value })}
                      >
                        <option value="Webhook">Webhook (escutar)</option>
                        <option value="Poll">Poll (consultar)</option>
                      </select>
                    </label>
                    <label className="erp-label">
                      <FieldLabel {...channelFieldHelp.apiMethod}>Método</FieldLabel>
                      <select
                        className="erp-input"
                        value={apiSettings.method}
                        onChange={(e) => patchApi({ method: e.target.value })}
                      >
                        <option value="GET">GET</option>
                        <option value="POST">POST</option>
                        <option value="PUT">PUT</option>
                        <option value="PATCH">PATCH</option>
                        <option value="DELETE">DELETE</option>
                        <option value="HEAD">HEAD</option>
                      </select>
                    </label>
                    <label className="erp-label md:col-span-2">
                      <FieldLabel {...channelFieldHelp.apiUrl}>URL</FieldLabel>
                      <input
                        className="erp-input"
                        value={apiSettings.url ?? ''}
                        onChange={(e) => patchApi({ url: e.target.value })}
                        placeholder="https://api.exemplo.com/v1/events"
                      />
                    </label>
                    <label className="erp-label md:col-span-2">
                      <FieldLabel {...channelFieldHelp.apiHeaders}>Headers (JSON)</FieldLabel>
                      <textarea
                        className="erp-input min-h-[88px] font-mono text-xs"
                        value={apiSettings.headersJson ?? ''}
                        onChange={(e) => patchApi({ headersJson: e.target.value })}
                        spellCheck={false}
                      />
                    </label>
                  </div>
                </FormCollapsePanel>

                <FormCollapsePanel
                  title="Saída (API out)"
                  summary={
                    apiSettings.outboundUrl
                      ? `${apiSettings.outboundMethod} ${apiSettings.outboundUrl}`
                      : 'Opcional · usa URL acima se vazio'
                  }
                  expanded={!!openPanels.apiOutbound}
                  onToggle={() => togglePanel('apiOutbound')}
                >
                  <p className="mb-3 text-xs text-secondary-500">
                    Usado quando este canal (ou outro que aponta para ele) envia respostas públicas
                    / N1 / auto-reply via HTTP. Placeholders: {'{{ticketId}}'}, {'{{ticketCode}}'},{' '}
                    {'{{externalConversationId}}'}, {'{{body}}'}, {'{{to}}'}.
                  </p>
                  <div className="erp-form-grid">
                    <label className="erp-label">
                      <FieldLabel {...channelFieldHelp.apiOutboundMethod}>Método</FieldLabel>
                      <select
                        className="erp-input"
                        value={apiSettings.outboundMethod}
                        onChange={(e) => patchApi({ outboundMethod: e.target.value })}
                      >
                        <option value="POST">POST</option>
                        <option value="PUT">PUT</option>
                        <option value="PATCH">PATCH</option>
                      </select>
                    </label>
                    <label className="erp-label md:col-span-2">
                      <FieldLabel {...channelFieldHelp.apiOutboundUrl}>URL de envio</FieldLabel>
                      <input
                        className="erp-input"
                        value={apiSettings.outboundUrl ?? ''}
                        onChange={(e) => patchApi({ outboundUrl: e.target.value })}
                        placeholder="https://api.exemplo.com/v1/messages (vazio = URL de entrada)"
                      />
                    </label>
                    <label className="erp-label md:col-span-2">
                      <FieldLabel {...channelFieldHelp.apiOutboundBody}>Body template</FieldLabel>
                      <textarea
                        className="erp-input min-h-[120px] font-mono text-xs"
                        value={apiSettings.outboundBodyTemplate ?? ''}
                        onChange={(e) => patchApi({ outboundBodyTemplate: e.target.value })}
                        spellCheck={false}
                      />
                    </label>
                  </div>
                </FormCollapsePanel>

                <FormCollapsePanel
                  title="Autenticação"
                  summary={apiAuthSummary}
                  expanded={!!openPanels.apiAuth}
                  onToggle={() => togglePanel('apiAuth')}
                >
                  <div className="erp-form-grid">
                    <label className="erp-label">
                      <FieldLabel {...channelFieldHelp.apiAuthType}>Tipo</FieldLabel>
                      <select
                        className="erp-input"
                        value={apiSettings.authType}
                        onChange={(e) => patchApi({ authType: e.target.value })}
                      >
                        <option value="None">None</option>
                        <option value="Bearer">Bearer Token</option>
                        <option value="Basic">Basic</option>
                        <option value="ApiKey">API Key</option>
                      </select>
                    </label>
                    {apiSettings.authType === 'Basic' ? (
                      <label className="erp-label">
                        <FieldLabel {...channelFieldHelp.apiAuthUsername}>Usuário</FieldLabel>
                        <input
                          className="erp-input"
                          value={apiSettings.authUsername ?? ''}
                          onChange={(e) => patchApi({ authUsername: e.target.value })}
                        />
                      </label>
                    ) : null}
                    {apiSettings.authType === 'ApiKey' ? (
                      <label className="erp-label">
                        <FieldLabel {...channelFieldHelp.apiApiKeyHeader}>Nome do header</FieldLabel>
                        <input
                          className="erp-input"
                          value={apiSettings.apiKeyHeaderName ?? ''}
                          onChange={(e) => patchApi({ apiKeyHeaderName: e.target.value })}
                          placeholder="X-Api-Key"
                        />
                      </label>
                    ) : null}
                    {apiSettings.authType !== 'None' ? (
                      <label className="erp-label">
                        <FieldLabel {...channelFieldHelp.apiAuthSecret}>
                          {apiSettings.authType === 'Basic' ? 'Senha' : 'Secret / Token'}
                        </FieldLabel>
                        <PasswordInput
                          className="erp-input pr-11 !mt-0"
                          wrapperClassName="mt-1 w-full"
                          autoComplete="new-password"
                          value={apiSettings.authSecret ?? ''}
                          onChange={(e) => patchApi({ authSecret: e.target.value })}
                          placeholder={
                            editingChannel?.apiSettings?.hasAuthSecret
                              ? 'Deixe em branco para manter'
                              : undefined
                          }
                        />
                      </label>
                    ) : null}
                  </div>
                </FormCollapsePanel>

                <FormCollapsePanel
                  title="Webhook"
                  summary={
                    apiSettings.mode === 'Webhook'
                      ? apiWebhookSummary
                      : 'Modo Poll — webhook opcional para push'
                  }
                  expanded={!!openPanels.apiWebhook}
                  onToggle={() => togglePanel('apiWebhook')}
                >
                  <label className="erp-label">
                    <FieldLabel {...channelFieldHelp.apiWebhookEndpoint}>Endpoint do canal</FieldLabel>
                    <input
                      className="erp-input font-mono text-xs"
                      readOnly
                      value={apiWebhookSummary}
                      onFocus={(e) => e.currentTarget.select()}
                    />
                  </label>
                  <label className="erp-label">
                    <FieldLabel {...channelFieldHelp.apiWebhookSecret}>Webhook secret (HMAC)</FieldLabel>
                    <PasswordInput
                      className="erp-input pr-11 !mt-0"
                      wrapperClassName="mt-1 w-full"
                      autoComplete="new-password"
                      value={apiSettings.webhookSecret ?? ''}
                      onChange={(e) => patchApi({ webhookSecret: e.target.value })}
                      placeholder={
                        editingChannel?.apiSettings?.hasWebhookSecret
                          ? 'Deixe em branco para manter'
                          : 'Valida X-Webhook-Signature / X-Signature'
                      }
                    />
                  </label>
                  <p className="text-xs text-secondary-500">
                    Aceita JSON único, lista ou objeto com items/data/messages. Auth do canal (Bearer/Basic/API Key) também é validada na entrada, se configurada.
                  </p>
                </FormCollapsePanel>
              </div>
            ) : null}

            {form.channelType === 'Chat' ? (
              <div className="space-y-2">
                <FormCollapsePanel
                  title="Conexão do chat"
                  summary={chatConnectionSummary}
                  expanded={!!openPanels.chatConnection}
                  onToggle={() => togglePanel('chatConnection')}
                >
                  <div className="space-y-3">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                      <label className="erp-label min-w-0 flex-1">
                        <FieldLabel {...channelFieldHelp.chatBaseUrl} required={form.isEnabled}>
                          URL base
                        </FieldLabel>
                        <input
                          className="erp-input"
                          value={chatSettings.baseUrl ?? ''}
                          onChange={(e) => patchChat({ baseUrl: e.target.value })}
                          placeholder="https://chat.exemplo.com"
                          required={form.isEnabled}
                        />
                      </label>
                      <label className="erp-label w-[8.5rem] shrink-0">
                        <FieldLabel {...channelFieldHelp.chatAccountId} required={form.isEnabled}>
                          Account ID
                        </FieldLabel>
                        <input
                          className="erp-input"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={chatSettings.accountId || ''}
                          onChange={(e) => {
                            const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
                            patchChat({ accountId: digits === '' ? 0 : Number(digits) });
                          }}
                          required={form.isEnabled}
                        />
                      </label>
                    </div>
                    <div className="erp-form-grid">
                      <label className="erp-label">
                        <FieldLabel {...channelFieldHelp.chatInboxId} required={form.isEnabled}>
                          Inbox ID
                        </FieldLabel>
                        <input
                          className="erp-input"
                          value={chatSettings.inboxId ?? ''}
                          onChange={(e) => patchChat({ inboxId: e.target.value })}
                          required={form.isEnabled}
                        />
                      </label>
                      <label className="erp-label">
                        <FieldLabel {...channelFieldHelp.chatAssistantName}>Assistente</FieldLabel>
                        <input
                          className="erp-input"
                          value={chatSettings.assistantName ?? ''}
                          onChange={(e) => patchChat({ assistantName: e.target.value })}
                          placeholder="Suporte N1 / Comercial"
                        />
                      </label>
                      <label className="erp-label md:col-span-2">
                        <FieldLabel
                          {...channelFieldHelp.chatApiAccessToken}
                          required={form.isEnabled && !editingChannel?.chatSettings?.hasApiAccessToken}
                        >
                          API access token
                        </FieldLabel>
                        <PasswordInput
                          className="erp-input pr-11 !mt-0"
                          wrapperClassName="mt-1 w-full"
                          autoComplete="new-password"
                          value={chatSettings.apiAccessToken ?? ''}
                          onChange={(e) => patchChat({ apiAccessToken: e.target.value })}
                          placeholder={
                            editingChannel?.chatSettings?.hasApiAccessToken
                              ? 'Deixe em branco para manter'
                              : undefined
                          }
                        />
                      </label>
                    </div>
                  </div>
                </FormCollapsePanel>

                <FormCollapsePanel
                  title="Webhook"
                  summary={chatWebhookSummary}
                  expanded={!!openPanels.chatWebhook}
                  onToggle={() => togglePanel('chatWebhook')}
                >
                  <label className="erp-label">
                    <FieldLabel {...channelFieldHelp.chatWebhookEndpoint}>Endpoint do canal</FieldLabel>
                    <input
                      className="erp-input font-mono text-xs"
                      readOnly
                      value={chatWebhookSummary}
                      onFocus={(e) => e.currentTarget.select()}
                    />
                  </label>
                  <label className="erp-label">
                    <FieldLabel {...channelFieldHelp.chatWebhookSecret}>Webhook secret (HMAC)</FieldLabel>
                    <PasswordInput
                      className="erp-input pr-11 !mt-0"
                      wrapperClassName="mt-1 w-full"
                      autoComplete="new-password"
                      value={chatSettings.webhookSecret ?? ''}
                      onChange={(e) => patchChat({ webhookSecret: e.target.value })}
                      placeholder={
                        editingChannel?.chatSettings?.hasWebhookSecret
                          ? 'Deixe em branco para manter'
                          : 'Opcional — valida X-Chat-Signature'
                      }
                    />
                  </label>
                  <p className="text-xs text-secondary-500">
                    Eventos: message_created (incoming) e conversation_resolved / conversation_status_changed.
                  </p>
                </FormCollapsePanel>
              </div>
            ) : null}

            <div className="mt-3">
              <SupportRoutingRulesField
                label="Regras de roteamento"
                description="Escolha o efeito desejado e informe padrões de e-mail (ex.: @eveo.com.br). Não é preciso digitar o nome técnico da chave."
                helpDescription={channelFieldHelp.routingRules.description}
                helpExample={channelFieldHelp.routingRules.example}
                value={form.routingRulesJson}
                onChange={(routingRulesJson) => setForm({ ...form, routingRulesJson })}
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isEnabled}
                onChange={(e) => setForm({ ...form, isEnabled: e.target.checked })}
              />
              <FieldLabel {...channelFieldHelp.isEnabled}>Canal ativo</FieldLabel>
            </label>
            <FormActionBar
              onCancel={() => collapseForm(resetForm)}
              cancelLabel="Cancelar"
            >
              <button type="submit" disabled={saving} className="erp-btn-primary">
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </FormActionBar>
          </div>
        </form>
        </CollapsibleFormSection>
      ) : null}

      {loading ? (
        <LoadingSpinner label="Carregando canais..." />
      ) : (
        <div className="erp-card overflow-x-auto">
          <table className="erp-table w-full">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Tipo</th>
                <th>Modo</th>
                <th>Inbox</th>
                <th>Conexão</th>
                <th>Empresa</th>
                <th>Status</th>
                {canWrite ? <th className="w-32" /> : null}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td className="font-medium">{item.name}</td>
                  <td>{CHANNEL_LABELS[item.channelType] ?? item.channelType}</td>
                  <td>
                    {item.attendanceModeLabel
                      ?? ATTENDANCE_MODE_LABELS[(item.attendanceMode as SupportChannelAttendanceMode) || 'Manual']
                      ?? item.attendanceMode
                      ?? 'Manual'}
                  </td>
                  <td>{item.externalInboxId ?? '—'}</td>
                  <td>
                    {[
                      item.channelType === 'Email'
                        ? (item.emailSettings?.imapHost ?? 'Fallback das configurações')
                        : item.channelType === 'Api'
                          ? (item.apiSettings?.url
                            ? `${item.apiSettings.method} · ${item.apiSettings.mode}`
                            : item.apiSettings?.mode ?? '—')
                          : item.channelType === 'Chat'
                            ? (item.chatSettings?.assistantName
                              || item.chatSettings?.inboxId
                              || item.externalInboxId
                              || '—')
                            : '—',
                      item.attendanceMode === 'Assistant' || item.assistantSettings?.enabled
                        ? 'N1'
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </td>
                  <td>{item.defaultCompanyName ?? '—'}</td>
                  <td>
                    <ActiveStatusBadge active={item.isEnabled} />
                  </td>
                  {canWrite ? (
                    <td>
                      <div className="flex gap-1">
                        <EditIconButton
                          label="Editar canal"
                          onClick={() => startEdit(item)}
                          disabled={duplicatingId === item.id}
                        />
                        <DuplicateIconButton
                          label={duplicatingId === item.id ? 'Duplicando...' : 'Duplicar canal'}
                          disabled={duplicatingId === item.id}
                          onClick={() => void handleDuplicate(item)}
                        />
                        <DeleteIconButton
                          label="Excluir canal"
                          disabled={duplicatingId === item.id}
                          onClick={() => void handleDelete(item)}
                        />
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageShell>
  );
}
